import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import {
  Phone,
  Building2,
  Pill,
  Banknote,
  Heart,
  Shield,
  Loader2,
  MapPin,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Star,
} from "lucide-react";

const CATEGORY_META = {
  atm: { label: "ATMs", icon: Banknote, color: "text-emerald-400", bg: "bg-emerald-500/10" },
  pharmacy: { label: "Pharmacies", icon: Pill, color: "text-sky-400", bg: "bg-sky-500/10" },
  hospital: { label: "Hospitals", icon: Heart, color: "text-rose-400", bg: "bg-rose-500/10" },
};

export const NearbyEssentials = ({ countryCode, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(!compact);
  const [activeTab, setActiveTab] = useState("emergency");

  useEffect(() => {
    if (countryCode) fetchEssentials();
  }, [countryCode]);

  const fetchEssentials = async () => {
    try {
      const res = await axios.get(`${API}/essentials/${countryCode}`, {
        withCredentials: true,
      });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="glass-card p-4 flex justify-center" data-testid="essentials-loading">
        <Loader2 className="w-5 h-5 animate-spin text-rose-400" />
      </div>
    );
  }

  if (!data) return null;

  const { emergency_numbers, nearby } = data;
  const tabs = [
    { id: "emergency", label: "Emergency" },
    ...Object.keys(nearby || {}).map((k) => ({ id: k, label: CATEGORY_META[k]?.label || k })),
  ];

  return (
    <div className="glass-card p-4" data-testid="nearby-essentials">
      {/* Header */}
      <button
        className="w-full flex items-center justify-between"
        onClick={() => compact && setExpanded(!expanded)}
        data-testid="essentials-toggle"
      >
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Shield className="w-4 h-4 text-rose-400" />
          Nearby Essentials
        </h3>
        {compact && (
          expanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />
        )}
      </button>

      {expanded && (
        <div className="mt-3">
          {/* Tabs */}
          <div className="flex gap-1 overflow-x-auto pb-2 scrollbar-hide mb-3">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? "bg-rose-500/20 text-rose-400"
                    : "bg-white/5 text-zinc-400 hover:bg-white/10"
                }`}
                data-testid={`essentials-tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Emergency Numbers */}
          {activeTab === "emergency" && (
            <div className="grid grid-cols-3 gap-2" data-testid="emergency-numbers">
              {[
                { key: "police", label: "Police", icon: Shield, color: "text-blue-400", bg: "bg-blue-500/10" },
                { key: "ambulance", label: "Ambulance", icon: Heart, color: "text-rose-400", bg: "bg-rose-500/10" },
                { key: "fire", label: "Fire", icon: Phone, color: "text-orange-400", bg: "bg-orange-500/10" },
              ].map((svc) => (
                <a
                  key={svc.key}
                  href={`tel:${emergency_numbers[svc.key]}`}
                  className={`${svc.bg} rounded-xl p-3 flex flex-col items-center gap-1.5 hover:opacity-80 transition-opacity`}
                  data-testid={`emergency-${svc.key}`}
                >
                  <svc.icon className={`w-5 h-5 ${svc.color}`} />
                  <span className="text-xs text-zinc-400">{svc.label}</span>
                  <span className={`text-lg font-bold ${svc.color}`}>{emergency_numbers[svc.key]}</span>
                </a>
              ))}
            </div>
          )}

          {/* Nearby Places */}
          {activeTab !== "emergency" && nearby[activeTab] && (
            <div className="space-y-2" data-testid={`nearby-${activeTab}`}>
              {nearby[activeTab].length === 0 ? (
                <p className="text-xs text-zinc-500 text-center py-4">No places found nearby</p>
              ) : (
                nearby[activeTab].map((place, idx) => {
                  const meta = CATEGORY_META[activeTab] || {};
                  const Icon = meta.icon || Building2;
                  return (
                    <div
                      key={idx}
                      className="flex items-start gap-3 p-2.5 rounded-lg bg-white/[0.03] border border-white/5"
                      data-testid={`nearby-place-${activeTab}-${idx}`}
                    >
                      <div className={`w-8 h-8 rounded-lg ${meta.bg || "bg-white/5"} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-4 h-4 ${meta.color || "text-zinc-400"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{place.name}</p>
                        <p className="text-xs text-zinc-500 truncate">{place.address}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {place.open_now !== null && place.open_now !== undefined && (
                            <span className={`text-xs font-medium ${place.open_now ? "text-emerald-400" : "text-zinc-500"}`}>
                              {place.open_now ? "Open now" : "Closed"}
                            </span>
                          )}
                          {place.rating && (
                            <span className="flex items-center gap-0.5 text-xs text-amber-400">
                              <Star className="w-3 h-3 fill-current" />
                              {place.rating}
                            </span>
                          )}
                        </div>
                      </div>
                      {place.lat && place.lng && (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${place.lat},${place.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors flex-shrink-0"
                          data-testid={`nearby-map-link-${idx}`}
                        >
                          <MapPin className="w-3.5 h-3.5 text-zinc-400" />
                        </a>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
